import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import translate from '../../translate';
import LevelCard from '../../../sections/profile/sidePanel/components/LevelCard';
import LoggedUserContext from '../../../contextProviders/LoggedUserContext';
import ConfigsContext from '../../../contextProviders/ConfigsContext';
import Notifications from '../../../services/notifications';
import i18n from '../../../services/i18n';
import ShareModal from '../../../sections/academy/modal/ShareModal';
import { useModal } from '../../../services/customHooks';
import ChatService from '../../../services/chatService';
import IsTestModeContext from '../../../contextProviders/IsTestModeContext';

function DistributorBanner({ t }) {
  const { loggedUser } = useContext(LoggedUserContext);
  const { tenant } = useContext(ConfigsContext);
  const { isTestMode } = useContext(IsTestModeContext);
  if (isTestMode) return null;

  const directSellingActive = tenant?.settings?.directSelling === true;
  const distributorPageAvailableToAllTypes =
    tenant?.settings?.distributorPageAvailableToAllTypes;
  const portalUrl = tenant?.configs?.userPortal.endsWith('/')
    ? tenant?.configs.userPortal
    : `${tenant?.configs.userPortal}/`;
  const pageUrl = `${portalUrl}user/${loggedUser.userPageName}`;

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(pageUrl).then(() => {
      Notifications.showNotificationSuccess(
        i18n.__('success'),
        i18n.__('share_modal_copySuccess'),
      );
    });
  };

  const twitterShareUrl =
    'https://twitter.com/intent/tweet?' +
    `text=${t('share.modal.userPageName.text')}` +
    `&url=${encodeURIComponent(pageUrl)}`;

  const facebookAppId = tenant.configs.facebookAppId || null;
  const facebookShareUrl =
    'https://www.facebook.com/dialog/share?' +
    `app_id=${facebookAppId}` +
    '&display=page' +
    `&href=${encodeURIComponent(pageUrl)}` +
    `&redirect_url=${encodeURIComponent(
      typeof window !== 'undefined' ? window.location.href : '',
    )}`;

  const linkedInShareUrl =
    'https://www.linkedin.com/sharing/share-offsite?' +
    `url=${encodeURIComponent(pageUrl)}`;

  const mailShareUrl =
    'mailto:?' +
    `body=${t('share.modal.userPageName.text')}: ${pageUrl}` +
    `&subject=${'share.modal.userPageName.title'}`;

  const [showModal, setShowModal] = useModal({ show: false, data: null });
  const openShareModal = () => {
    setShowModal({ show: true, data: null });
  };

  const openInviteModal = () => {
    ChatService.showUserInvitationPopup();
  };

  const showWidget = distributorPageAvailableToAllTypes
    ? true
    : loggedUser?.type === 'collaborator';

  // Only for collaborators if the option is enabled on the tenant
  return showWidget ? (
    <>
      {showModal.show && (
        <ShareModal
          isShareUserPage
          url={pageUrl}
          onClose={() => setShowModal({ show: false, data: null })}
        />
      )}
      <div className="distributor-banner-container">
        <div className="distributor-banner-content">
          <div className="level-card-container">
            <LevelCard
              user={loggedUser}
              directSellingActive={directSellingActive}
            />
          </div>
          <div className="distributor-share-container">
            <div className="distributor-share-header">
              <h3>{t('profile.share.userPage')}</h3>
              <button
                type="button"
                className="btn btn-dynamic"
                onClick={openInviteModal}
              >
                {t('invite_new_recommendation')}
              </button>
            </div>
            <div className="distributor-share-links">
              <div className="distributor-share-link">
                <input type="text" value={pageUrl} disabled />
                <div className="clipboard-container" onClick={copyToClipboard}>
                  <i className="la la-clipboard" />
                </div>
              </div>
              <div className="distributor-share-socials">
                <p
                  className="btn btn-metal m-btn m-btn--icon btn-lg m-btn--icon-only m-btn--pill qrCode"
                  onClick={openShareModal}
                >
                  <img src="/images/qr-code.svg" alt="qr-code" />
                </p>
                {facebookAppId && (
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={facebookShareUrl}
                    className="btn btn-metal m-btn m-btn--icon btn-lg m-btn--icon-only m-btn--pill"
                  >
                    <i className="socicon-facebook" />
                  </a>
                )}
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={linkedInShareUrl}
                  className="btn btn-metal m-btn m-btn--icon btn-lg m-btn--icon-only m-btn--pill"
                >
                  <i className="socicon-linkedin" />
                </a>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={twitterShareUrl}
                  className="btn btn-metal m-btn m-btn--icon btn-lg m-btn--icon-only m-btn--pill"
                >
                  <i className="socicon-twitter" />
                </a>
                <a
                  href={mailShareUrl}
                  className="btn btn-metal m-btn m-btn--icon btn-lg m-btn--icon-only m-btn--pill"
                >
                  <i className="socicon-mail" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  ) : null;
}

DistributorBanner.propTypes = {
  t: PropTypes.func.isRequired,
};

export default React.memo(translate(DistributorBanner));
